import type { PerformanceSnapshot, ProcessPerformanceSample } from '@shared/performance-monitor'

export const MAX_PROCESS_ROWS = 9
export const METRIC_HISTORY_LENGTH = 60
export const CHART_HEIGHT = 36
export const TOOLTIP_CLASS = 'max-w-[240px] whitespace-normal'

export type Severity = 'ok' | 'warn' | 'hot' | 'critical'

export const SEVERITY_TEXT: Record<Severity, string> = {
  ok: 'text-success-subtle',
  warn: 'text-yellow-subtle',
  hot: 'text-warning-subtle',
  critical: 'text-danger-subtle',
}

export const THRESHOLDS = {
  cpuPercent: [25, 60, 120] as const,
  memoryMB: [300, 600, 1200] as const,
  wakeups: [50, 150, 400] as const,
  energy: [25, 75, 200] as const,
  powerMw: [250, 750, 2000] as const,
  fanoutPerSecond: [5, 15, 40] as const,
  fanoutPerMinute: [300, 900, 2400] as const,
  axMs: [10, 30, 80] as const,
}

export type PerformanceTotals = PerformanceSnapshot['totals']
export type TrendMetric = keyof PerformanceTotals

export interface MetricChartConfig {
  label: string
  unit: string
  digits: number
  thresholds: readonly [number, number, number]
  color: string
}

interface TotalsMetricChartConfig extends MetricChartConfig {
  metric: TrendMetric
}

export interface PerformanceHistorySample {
  totals: PerformanceTotals
  ipcFramesPerSecond?: number
  slowestAxWalkMs?: number
}

export type SortColumn =
  | 'label'
  | 'cpuPercent'
  | 'memoryMB'
  | 'idleWakeupsPerSecond'
  | 'energyImpact'
  | 'powerMilliwatts'

export interface SortState {
  column: SortColumn
  descending: boolean
}

export const METRIC_CHARTS: Array<TotalsMetricChartConfig> = [
  {
    metric: 'cpuPercent',
    label: 'cpu',
    unit: '%',
    digits: 1,
    thresholds: THRESHOLDS.cpuPercent,
    color: 'text-teal-subtle',
  },
  {
    metric: 'memoryMB',
    label: 'memory',
    unit: 'mb',
    digits: 0,
    thresholds: THRESHOLDS.memoryMB,
    color: 'text-info-subtle',
  },
  {
    metric: 'idleWakeupsPerSecond',
    label: 'wakeups',
    unit: '/s',
    digits: 0,
    thresholds: THRESHOLDS.wakeups,
    color: 'text-indigo-subtle',
  },
  {
    metric: 'energyImpact',
    label: 'energy',
    unit: 'nrg',
    digits: 0,
    thresholds: THRESHOLDS.energy,
    color: 'text-purple-subtle',
  },
  {
    metric: 'powerMilliwatts',
    label: 'power',
    unit: 'mW',
    digits: 0,
    thresholds: THRESHOLDS.powerMw,
    color: 'text-pink-subtle',
  },
]

export const IPC_CHART: MetricChartConfig = {
  label: 'ipc fan-out',
  unit: '/s',
  digits: 1,
  thresholds: THRESHOLDS.fanoutPerSecond,
  color: 'text-brown-subtle',
}

export const AX_CHART: MetricChartConfig = {
  label: 'slowest ax walk',
  unit: 'ms',
  digits: 1,
  thresholds: THRESHOLDS.axMs,
  color: 'text-warning-subtle',
}

export const COLUMN_TOOLTIPS: Record<SortColumn, string> = {
  label: 'Process name — click any header to sort; click again to flip direction',
  cpuPercent: 'CPU usage since the last 1s sample, in % of one core',
  memoryMB: 'Working set — memory currently pinned to physical RAM',
  idleWakeupsPerSecond:
    'Idle wakeups per second. High values drain battery even at low CPU% — each wakeup pulls the CPU out of low-power idle',
  energyImpact:
    "Energy-impact score (unitless): CPU% + 0.35 × wakeups/s — same shape as Activity Monitor's Energy Impact, not calibrated to its exact units. Catches wakeup-heavy processes whose cost lands outside their own power counter",
  powerMilliwatts:
    "Measured average power over the last second, from the kernel's per-process energy counter (Apple Silicon only). Excludes display and work done on the process's behalf elsewhere (e.g. WindowServer)",
}

export const DEFAULT_SORT: SortState = { column: 'label', descending: false }

export function historyValues(
  history: Array<PerformanceHistorySample>,
  select: (sample: PerformanceHistorySample) => number | undefined,
): Array<number> {
  return history.flatMap((sample) => {
    const value = select(sample)
    return value === undefined ? [] : [value]
  })
}

export function severityFor(value: number, [warn, hot, critical]: readonly [number, number, number]): Severity {
  if (value >= critical) {
    return 'critical'
  }
  if (value >= hot) {
    return 'hot'
  }
  if (value >= warn) {
    return 'warn'
  }
  return 'ok'
}

export function sortProcesses(
  processes: Array<ProcessPerformanceSample>,
  sort: SortState,
): Array<ProcessPerformanceSample> {
  const direction = sort.descending ? -1 : 1
  return [...processes].sort((a, b) => {
    const comparison =
      sort.column === 'label' ? a.label.localeCompare(b.label) : (a[sort.column] ?? 0) - (b[sort.column] ?? 0)
    return comparison !== 0 ? comparison * direction : a.pid - b.pid
  })
}
